/**
 * Termination Reason Select Component
 * US-CONT-006: Terminate Contract
 * Dropdown selector for contract termination reasons
 */

import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import { AlertCircle } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { TerminationReason } from '../types/contract-termination.types';
import {
  TERMINATION_REASON_LABELS,
  TERMINATION_REASON_DESCRIPTIONS,
  TerminationReasonMap
} from '../types/contract-termination.types';

interface TerminationReasonSelectProps {
  value: TerminationReason | '';
  onChange: (value: TerminationReason) => void;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
}

export function TerminationReasonSelect({
  value,
  onChange,
  error,
  disabled = false,
  required = true,
  className
}: TerminationReasonSelectProps) {
  const reasons = Object.keys(TerminationReasonMap) as TerminationReason[];

  return (
    <div className={cn('space-y-2', className)}>
      <Label htmlFor="termination-reason" className="text-sm font-medium">
        Termination Reason
        {required && <span className="text-destructive ml-1" aria-label="required">*</span>}
      </Label>

      <Select
        value={value}
        onValueChange={onChange}
        disabled={disabled}
        required={required}
      >
        <SelectTrigger
          id="termination-reason"
          className={cn(
            'w-full',
            error && 'border-destructive focus:ring-destructive'
          )}
          aria-invalid={!!error}
          aria-describedby={error ? 'termination-reason-error' : undefined}
        >
          <SelectValue placeholder="Select a reason for termination" />
        </SelectTrigger>

        <SelectContent>
          {reasons.map((reason) => (
            <SelectItem key={reason} value={reason}>
              <div className="flex flex-col">
                <span className="font-medium">{TERMINATION_REASON_LABELS[reason]}</span>
                <span className="text-xs text-muted-foreground mt-0.5">
                  {TERMINATION_REASON_DESCRIPTIONS[reason]}
                </span>
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {error && (
        <div
          id="termination-reason-error"
          className="flex items-center gap-1.5 text-sm text-destructive"
          role="alert"
          aria-live="polite"
        >
          <AlertCircle className="h-4 w-4" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {!error && value && (
        <p className="text-xs text-muted-foreground" aria-live="polite">
          {TERMINATION_REASON_DESCRIPTIONS[value]}
        </p>
      )}
    </div>
  );
}

/**
 * Compact version for smaller layouts
 */
interface CompactTerminationReasonSelectProps extends TerminationReasonSelectProps {
  showDescription?: boolean;
}

export function CompactTerminationReasonSelect({
  value,
  onChange,
  error,
  disabled = false,
  required = true,
  showDescription = false,
  className
}: CompactTerminationReasonSelectProps) {
  const reasons = Object.keys(TerminationReasonMap) as TerminationReason[];

  return (
    <div className={cn('space-y-1', className)}>
      <Select
        value={value}
        onValueChange={onChange}
        disabled={disabled}
        required={required}
      >
        <SelectTrigger
          className={cn(
            'w-full h-9 text-sm',
            error && 'border-destructive focus:ring-destructive'
          )}
          aria-invalid={!!error}
        >
          <SelectValue placeholder="Select reason" />
        </SelectTrigger>

        <SelectContent>
          {reasons.map((reason) => (
            <SelectItem key={reason} value={reason} className="text-sm">
              {TERMINATION_REASON_LABELS[reason]}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {error && (
        <div className="flex items-center gap-1 text-xs text-destructive" role="alert">
          <AlertCircle className="h-3 w-3" aria-hidden="true" />
          <span>{error}</span>
        </div>
      )}

      {!error && showDescription && value && (
        <p className="text-xs text-muted-foreground">
          {TERMINATION_REASON_DESCRIPTIONS[value]}
        </p>
      )}
    </div>
  );
}
